import React, { useState, useCallback, useEffect } from 'react';
import moment from 'moment';

// components
import withBook from 'common/utils/components/withBook';

// selectors
import { createStructuredSelector } from 'reselect';
import {
    getUI,
    getDefaultJournal,
    getJournalList,
    getBookById,
    getAamJournal
} from 'redux/selectors';

// Actions
import { connect } from 'react-redux';
import { updateUI } from 'redux/actions/ui';
import * as BookActions from 'redux/actions/book';
import * as JournalActions from 'redux/actions/journal';
import * as TagActions from 'redux/actions/tag';
import * as EntryActions from 'redux/actions/entry';

// Dialogs
import BookSaveDialog from 'pages/Dialogs/BookSaveDialog';

// utils
import serializeUIToBook from 'common/utils/bookcreation/serializeUIToBook';
import deserializeUIToBook from 'common/utils/bookcreation/deserializeUIToBook';

// constants
import {
    book_creation_steps,
    BOOK_DEFAULT_TITLE,
    ENTRY_ALL,
    ENTRY_DO_NOT_PRINT,
    BOOK_STATUS_BUILDING,
    ENTRY_MR,
    BOOK_EXPORT_FORMAT,
    DEFAULT_TIMEZONE
} from 'common/constant';

// steps
import ExportFormatStep from 'pages/Dialogs/BookCreateDialog2/ExportFormatStep';
import JournalSelectStep from 'pages/Dialogs/BookCreateDialog2/JournalSelectStep';
import EntriesStep from 'pages/Dialogs/BookCreateDialog2/EntriesStep';
import AllAboutMeStep from 'pages/Dialogs/BookCreateDialog2/AllAboutMeStep';
import ColorOptionsStep from 'pages/Dialogs/BookCreateDialog2/ColorOptionsStep';
import PhotosOptionsStep from 'pages/Dialogs/BookCreateDialog2/PhotosOptionsStep';
import FrontCoverStep from 'pages/Dialogs/BookCreateDialog2/FrontCoverStep';
import SubTitleStep from 'pages/Dialogs/BookCreateDialog2/SubTitleStep';
import SpineStep from 'pages/Dialogs/BookCreateDialog2/SpineStep';
import BackCoverStep from 'pages/Dialogs/BookCreateDialog2/BackCoverStep';
import CustomIntroductionPageStep from 'pages/Dialogs/BookCreateDialog2/CustomIntroductionPageStep';
import MediaOptionsStep from 'pages/Dialogs/BookCreateDialog2/MediaOptionsStep';
import { openDialog } from 'common/utils/dialog-utils';
import BookCreateSuccessDialog from 'pages/Dialogs/BookCreateSuccessDialog';

const BookCreateDialog = ({
    open,
    _close,
    dispatch,
    defaultJournal,
    journal,
    ui,
    book,
    aamJournal,
    step: propsStep = 1
}) => {
    const [step, setStep] = useState(propsStep);
    const [isCreating, seIsCreating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [uiInitialized, setUIInitialized] = useState(false);

    // component did mount
    useEffect(() => {
        // fetch data
        dispatch(JournalActions.fetch());
        dispatch(TagActions.fetch());
        return () => {};
    }, [dispatch, book]);

    useEffect(() => {
        if (book && !uiInitialized && journal?.journals) {
            const newUI = deserializeUIToBook(book, journal?.journals || []);

            dispatch(updateUI(Object.assign({}, newUI)));

            setStep(propsStep);
            setUIInitialized(true);
        } else if (!book && !uiInitialized) {
            // default book creation params
            dispatch(
                updateUI({
                    aam: ENTRY_MR,
                    entryRange: ENTRY_ALL,
                    minDate: undefined,
                    maxDate: undefined,
                    color: true,
                    photos: true,
                    selectedJournal: undefined,
                    title: BOOK_DEFAULT_TITLE,
                    subtitle: '',
                    spine: BOOK_DEFAULT_TITLE,
                    back_cover: '',
                    custom_introduction: '',
                    export_format: BOOK_EXPORT_FORMAT['6_9_FORMAT'],
                    timezone: DEFAULT_TIMEZONE
                })
            );

            setUIInitialized(true);
        }
    }, [book, ui, uiInitialized, dispatch, journal, propsStep]);

    useEffect(() => {
        // initiate ui.newBook
        dispatch(
            updateUI({
                newBook: {
                    export_format: '6X9',
                    title: 'My JRNL Book',
                    book_builder_status: 26,
                    timezone_offset: moment().format('ZZ'),
                    journal: defaultJournal?.id
                }
            })
        );
        return () => {};
    }, [defaultJournal, dispatch]);

    const bookCreationHandler = useCallback(
        (response) => {
            dispatch(
                updateUI({
                    book_extra_journals: [],
                    book_mission_journals: []
                })
            );

            // open book creation success modal
            openDialog(BookCreateSuccessDialog, {
                bookId: response.payload.data.id
            });
        },
        [dispatch]
    );

    const createBook = useCallback(() => {
        if (isCreating) {
            return;
        }
        const newBook = serializeUIToBook(ui, !book?.id, false);
        seIsCreating(true);

        if (book?.id && book?.book_builder_status !== BOOK_STATUS_BUILDING) {
            dispatch(
                BookActions.update(
                    {
                        ...book,
                        ...newBook
                    },
                    { freeze_status: true }
                )
            )
                .then((response) => {
                    if (response?.payload?.status === 202) {
                        bookCreationHandler(response);
                    } else {
                        seIsCreating(false);
                    }
                })
                .catch(() => {
                    seIsCreating(false);
                });
        } else {
            dispatch(BookActions.create(newBook))
                .then((response) => {
                    if (response?.payload?.status === 201) {
                        bookCreationHandler(response);
                    } else {
                        seIsCreating(false);
                    }
                })
                .catch(() => {
                    seIsCreating(false);
                });
        }
    }, [ui, dispatch, bookCreationHandler, book, isCreating]);

    const fetchEntryCount = useCallback(
        (_ui = ui) => {
            const {
                book_extra_journals = [],
                book_tags = [],
                book_mission_journals = [],
                book_select_journal = true
            } = _ui;

            const handler = (response) => {
                if (response.payload.status === 200) {
                    dispatch(
                        updateUI({
                            selected_entries_count_fetching: false,
                            selected_entries_count:
                                response.payload?.data?.meta?.total_count || 0
                        })
                    );
                }
            };

            const defaultHandler = () => {
                dispatch(
                    updateUI({
                        selected_entries_count_fetching: false,
                        selected_entries_count: 0
                    })
                );
            };

            if (book_select_journal === true) {
                if (book_extra_journals.length > 0) {
                    dispatch(
                        EntryActions.fetchEntriesWithOptions({
                            by_journal: book_extra_journals
                                .map((item) => item.id)
                                .join(',')
                        })
                    ).then(handler);
                } else {
                    defaultHandler();
                }
            } else if (book_select_journal === 'mission_chapters') {
                if (book_mission_journals.length > 0) {
                    dispatch(
                        EntryActions.fetchEntriesWithOptions({
                            by_journal: book_mission_journals
                                .map((item) => item.id)
                                .join(',')
                        })
                    ).then(handler);
                } else {
                    defaultHandler();
                }
            } else {
                if (book_tags.length > 0) {
                    dispatch(
                        EntryActions.fetchEntriesWithOptions({
                            tags__in: book_tags.map((item) => item.id).join(',')
                        })
                    ).then(handler);
                } else {
                    defaultHandler();
                }
            }

            dispatch(
                updateUI({
                    selected_entries_count_fetching: true
                })
            );
        },
        [ui, dispatch]
    );

    const fetchEntryMinAndMaxDate = useCallback(() => {
        const {
            entryRange,
            book_extra_journals = [],
            book_tags = [],
            book_mission_journals = [],
            book_select_journal = true
        } = ui;
        const options = {};
        if (entryRange === ENTRY_ALL) {
            if (book_select_journal === true) {
                // options['extra_journals'] = book_extra_journals
                //     .map((item) => item.id)
                //     .join(',');
                options['by_journal'] = book_extra_journals
                    .map((item) => item.id)
                    .join(',');
            } else if (book_select_journal === 'mission_chapters') {
                options['by_journal'] = book_mission_journals
                    .map((item) => item.id)
                    .join(',');
            } else {
                options['tags__in'] = book_tags
                    .map((item) => item.id)
                    .join(',');
            }
        } else if (entryRange === ENTRY_DO_NOT_PRINT) {
            options['entry_type'] = 'QA';
        }

        if (Object.keys(options).length > 0) {
            dispatch(
                EntryActions.fetchEntriesWithOptions({
                    ...options,
                    order_by: '-entry_date',
                    limit: 1
                })
            ).then((response) => {
                if (
                    response.payload.status === 200 &&
                    response.payload?.data?.objects.length > 0
                ) {
                    dispatch(
                        updateUI({
                            maxDate:
                                response.payload.data.objects[0].entry_date,
                            isSetIfNull: true
                        })
                    );
                }
            });

            dispatch(
                EntryActions.fetchEntriesWithOptions({
                    ...options,
                    order_by: 'entry_date',
                    limit: 1
                })
            ).then((response) => {
                if (
                    response.payload.status === 200 &&
                    response.payload?.data?.objects.length > 0
                ) {
                    dispatch(
                        updateUI({
                            minDate:
                                response.payload.data.objects[0].entry_date,
                            isSetIfNull: true
                        })
                    );
                }
            });
        }
    }, [ui, dispatch]);

    const fetchEntryMinAndMaxDateForAAM = useCallback(() => {
        const { aamMinDate, aamMaxDate } = ui;
        if (!aamMaxDate || !aamMinDate) {
            const options = {};
            if (aamJournal) {
                // options['extra_journals'] = book_extra_journals
                //     .map((item) => item.id)
                //     .join(',');
                options['by_journal'] = aamJournal.id;
            }

            if (Object.keys(options).length > 0) {
                dispatch(
                    EntryActions.fetchEntriesWithOptions({
                        ...options,
                        order_by: '-entry_date',
                        limit: 1
                    })
                ).then((response) => {
                    if (
                        response.payload.status === 200 &&
                        response.payload?.data?.objects.length > 0
                    ) {
                        const aamMaxDate =
                            response.payload.data.objects[0].entry_date;
                        const aamMaxDateConv = moment(aamMaxDate).toDate();
                        dispatch(
                            updateUI({
                                aamMaxDate: aamMaxDateConv
                            })
                        );
                    }
                });

                dispatch(
                    EntryActions.fetchEntriesWithOptions({
                        ...options,
                        order_by: 'entry_date',
                        limit: 1
                    })
                ).then((response) => {
                    if (
                        response.payload.status === 200 &&
                        response.payload?.data?.objects.length > 0
                    ) {
                        const aamMinDate =
                            response.payload.data.objects[0].entry_date;
                        const aamMinDateConv = moment(aamMinDate).toDate();
                        dispatch(
                            updateUI({
                                aamMinDate: aamMinDateConv
                            })
                        );
                    }
                });
            }
        }
    }, [ui, dispatch, aamJournal]);

    // handlers
    const onNextStep = useCallback(() => {
        if (isCreating) {
            return;
        }
        createBook();
    }, [createBook, isCreating]);

    // const onPrevStep = useCallback(() => {
    //     if (step > 1) {
    //         setStep(step - 1);
    //     }
    // }, [step]);

    const onChangeJournal = useCallback(
        (journal) => {
            dispatch(
                updateUI({
                    newBook: {
                        journal
                    }
                })
            );
            fetchEntryCount();
        },
        [dispatch, fetchEntryCount]
    );

    const bookCancelHandler = useCallback(
        (response) => {
            dispatch(
                updateUI({
                    dialogs: [
                        withBook(BookSaveDialog, response?.payload?.data.id)
                    ]
                })
            );
        },
        [dispatch]
    );

    const onCloseDialog = useCallback(() => {
        if (isCreating === true) {
            return;
        }
        const newBook = serializeUIToBook(
            ui,
            !book?.id,
            true,
            book_creation_steps[step - 1]
        );

        setIsSaving(true);
        if (book?.id && book?.book_builder_status !== BOOK_STATUS_BUILDING) {
            dispatch(
                BookActions.update(
                    {
                        ...book,
                        ...newBook
                    },
                    { freeze_status: true }
                )
            ).then((response) => {
                if (response?.payload?.status === 202) {
                    bookCancelHandler(response);
                }
            });
        } else {
            dispatch(BookActions.create(newBook)).then((response) => {
                if (response?.payload?.status === 201) {
                    bookCancelHandler(response);
                }
            });
        }
    }, [dispatch, step, ui, bookCancelHandler, book, isCreating]);

    // const renderLineStepItem = useCallback((step, current) => {
    //     const className = `${step >= current ? 'done-step' : ''} ${
    //         step === current ? 'active-step' : ''
    //     }`;
    //     return (
    //         <li key={current} className={className}>
    //             <div></div>
    //         </li>
    //     );
    // }, []);

    const onKeyDown = useCallback((e) => {
        const isEnterPress = e.code === 'Enter' || e?.keyCode === 13;
        if (isEnterPress && e.target.tagName.toLowerCase() === 'button') {
            e.preventDefault();
        }
    }, []);

    const canPreview = useCallback(() => {
        return (
            ui.stepsFilledIn.ExportFormatStep &&
            ui.stepsFilledIn.JournalSelectStep &&
            ui.stepsFilledIn.EntriesStep &&
            ui.stepsFilledIn.MediaOptionsStep &&
            ui.stepsFilledIn.AllAboutMeStep &&
            ui.stepsFilledIn.ColorOptionsStep &&
            ui.stepsFilledIn.PhotosOptionsStep &&
            ui.stepsFilledIn.FrontCoverStep &&
            ui.stepsFilledIn.SubTitleStep &&
            ui.stepsFilledIn.SpineStep &&
            ui.stepsFilledIn.BackCoverStep &&
            ui.stepsFilledIn.CustomIntroductionPageStep
        );
    }, [ui]);

    // Keydown handler
    useEffect(() => {
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [onKeyDown]);

    return (
        <div className="modal-frame">
            <div className="modal-box modal-box--600">
                <div className="modal__header modal__header--button">
                    Create New Book
                    {isCreating && (
                        <div className="saving-text">
                            <div className="saving-gif pr-3"></div>
                            Creating...
                        </div>
                    )}
                    {isSaving && (
                        <div className="saving-text">
                            <div className="saving-gif pr-3"></div>
                            Saving...
                        </div>
                    )}
                    <button
                        style={{ pointerEvents: isCreating ? 'none' : 'auto' }}
                        disabled={!canPreview() || isCreating}
                        className="btn btn-gradient"
                        onClick={onNextStep}
                    >
                        {'Preview'}
                    </button>
                </div>

                <div className="modal__body">
                    <ExportFormatStep display={true} />
                    <JournalSelectStep
                        onChange={onChangeJournal}
                        display={true}
                        fetchEntryCount={fetchEntryCount}
                        initCall={() => {
                            fetchEntryCount();
                            fetchEntryMinAndMaxDate();
                        }}
                    />
                    <EntriesStep
                        display={true}
                        initCall={() => {
                            fetchEntryMinAndMaxDate();
                        }}
                    />
                    <MediaOptionsStep
                        display={true}
                        initCall={() => {
                            fetchEntryMinAndMaxDateForAAM();
                        }}
                    />
                    <AllAboutMeStep display={true} />
                    <ColorOptionsStep display={true} />
                    <PhotosOptionsStep display={true} />
                    <FrontCoverStep display={true} />
                    <SubTitleStep display={true} />
                    <SpineStep display={true} />
                    <BackCoverStep display={true} />
                    <CustomIntroductionPageStep display={true} />
                </div>
                <div className="modal__footer">
                    <button onClick={onCloseDialog} className="btn btn-outline">
                        Save and exit
                    </button>

                    <button
                        style={{ pointerEvents: isCreating ? 'none' : 'auto' }}
                        disabled={!canPreview() || isCreating}
                        className="btn btn-gradient"
                        onClick={onNextStep}
                    >
                        {'Preview'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const state = createStructuredSelector({
    ui: getUI,
    defaultJournal: getDefaultJournal,
    journal: getJournalList,
    book: getBookById,
    aamJournal: getAamJournal
});
export default connect(state)(BookCreateDialog);
